import { useTenantStore } from '@/store/tenantStore';
import {
  getOnBoardingImageLayout,
  getOnBoardingImageSize,
  getOnBoardingImageUrl,
  getOnBoardingSignUpImageUrl,
  getLogoImageSize,
  getWhiteLogoUrl,
  showOnBoardingLogo,
} from '@/services/tenant/tenantImages';
import { useLocalization } from '@/hooks/useLocalization';
import { translateDynamicContent } from '@/i18n';
import styles from './AuthLayout.module.css';

/**
 * OnboardingPanel — the brand panel beside the login, sign-up and MFA forms.
 *
 * Mirrors Flutter WebOnboardingPage (presentation/web/auth/web_onboarding/web_onboarding.dart):
 *   - primaryColor background unless the preference enableOnboardingBackgroundColor is false
 *   - the white logo (onBoardingPage.showLogo != false), 7.w in, sized like the header logo;
 *     tapping it calls `onLogoClick` (WebOnboardingPage.onLogout)
 *   - onBoardingPage.headerText (f16m, white) and footerText (f14m, white), translated from the
 *     language file's `dynamicContent` map
 *   - the onboarding image (or the sign-up image), in a box at the panel's left edge, fitted
 *     and aligned by onBoardingImageFitValue / onBoardingImageAlignment
 */

interface OnboardingPanelProps {
  /** The sign-up page shows onBoardingSignUpPageImage (WebSignUpPage onBoardingImage) */
  image: 'onBoarding' | 'signUp';
  onLogoClick: () => void;
}

export function OnboardingPanel({ image, onLogoClick }: OnboardingPanelProps) {
  const { content, preferences } = useTenantStore();
  useLocalization(); // re-render when the language file loads or the language changes

  const brandName = content?.contentSettings?.general?.name ?? '';
  const page = content?.contentSettings?.onBoardingPage;
  const headerText = page?.headerText ? translateDynamicContent(page.headerText) : '';
  const footerText = page?.footerText ? translateDynamicContent(page.footerText) : '';

  const showLogo = showOnBoardingLogo(content);
  const logoUrl = showLogo ? getWhiteLogoUrl(content) : undefined;
  const logoSize = getLogoImageSize(content);

  const isSignUp = image === 'signUp';
  const imageUrl = isSignUp ? getOnBoardingSignUpImageUrl(content) : getOnBoardingImageUrl(content);
  const imageSize = getOnBoardingImageSize(content, isSignUp);
  const imageLayout = getOnBoardingImageLayout(content);

  const brandBackground =
    (preferences as Record<string, unknown> | null)?.['enableOnboardingBackgroundColor'] !== false;

  return (
    <aside
      className={[
        styles.panel,
        brandBackground ? styles.panelBrand : '',
        showLogo ? '' : styles.panelNoLogo,
      ].join(' ')}
    >
      {showLogo && (
        <div className={styles.panelInset}>
          <button type="button" className={styles.panelLogoBtn} onClick={onLogoClick}>
            {logoUrl && <img src={logoUrl} alt={brandName} className={styles.panelLogo} style={logoSize} />}
          </button>
        </div>
      )}

      {headerText && <p className={`${styles.panelInset} ${styles.panelHeaderText}`}>{headerText}</p>}

      {imageUrl && (imageSize.height ? (
        <div
          className={styles.panelImage}
          style={{
            width: imageSize.width,
            height: imageSize.height,
            backgroundImage: `url(${JSON.stringify(imageUrl)})`,
            ...imageLayout,
          }}
          aria-hidden
        />
      ) : (
        // A sign-up image with imageHeight -1 keeps its own aspect ratio
        <img src={imageUrl} alt="" className={styles.panelImageFree} style={{ width: imageSize.width }} />
      ))}

      {footerText && <p className={`${styles.panelInset} ${styles.panelFooterText}`}>{footerText}</p>}
    </aside>
  );
}
